export interface DaySchedule {
  day: string;
  enabled: boolean;
  startTimeMins: number;
  endTimeMins: number;
}
export interface QuietHoursConfig {
  enabled: boolean;
  timeZone: string;
  schedule: DaySchedule[];
}

// Matches server/constants/activeHours.js SUPPORTED_TIMEZONES.
export const SUPPORTED_TIMEZONES: string[] = [
  'America/New_York', // Eastern Time
  'America/Chicago', // Central Time
  'America/Denver', // Mountain Time
  'America/Los_Angeles', // Pacific Time
  'America/St_Johns', // Newfoundland Time
  'America/Halifax', // Atlantic Time
  'America/Anchorage', // Alaska Time
  'Pacific/Honolulu', // Hawaii Time
];

export const DEFAULT_TIMEZONE = 'America/Chicago';

// Matches server/constants/quietHours.js DEFAULT_QUIET_HOURS
export const DEFAULT_QUIET_HOURS: QuietHoursConfig = {
  enabled: false,
  timeZone: DEFAULT_TIMEZONE,
  schedule: [
    { day: 'Sunday', enabled: false, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Monday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Tuesday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Wednesday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Thursday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Friday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Saturday', enabled: false, startTimeMins: 600, endTimeMins: 1200 },
  ],
};

export interface ActiveHoursConfig {
  timeZone: string;
  schedule: DaySchedule[];
}

// Matches server/constants/activeHours.js DEFAULT_ACTIVE_HOURS
export const DEFAULT_ACTIVE_HOURS: ActiveHoursConfig = {
  timeZone: DEFAULT_TIMEZONE,
  schedule: [
    { day: 'Sunday', enabled: false, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Monday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Tuesday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Wednesday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Thursday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Friday', enabled: true, startTimeMins: 600, endTimeMins: 1200 },
    { day: 'Saturday', enabled: false, startTimeMins: 600, endTimeMins: 1200 },
  ],
};

export interface ContactActiveHours {
  enabled: boolean;
  schedule: DaySchedule[];
}

// The quiet-hours feature config at organization.organizationSettings.quietHours.
// Distinct from the legacy organization.quietHours field.
export interface QuietHoursSettings {
  contactActiveHours?: ContactActiveHours;
  disableSendNow?: boolean;
}

export const DEFAULT_CONTACT_ACTIVE_HOURS: ContactActiveHours = {
  enabled: false,
  schedule: [
    { day: 'Sunday', enabled: false, startTimeMins: 480, endTimeMins: 1260 },
    { day: 'Monday', enabled: true, startTimeMins: 480, endTimeMins: 1260 },
    { day: 'Tuesday', enabled: true, startTimeMins: 480, endTimeMins: 1260 },
    { day: 'Wednesday', enabled: true, startTimeMins: 480, endTimeMins: 1260 },
    { day: 'Thursday', enabled: true, startTimeMins: 480, endTimeMins: 1260 },
    { day: 'Friday', enabled: true, startTimeMins: 480, endTimeMins: 1260 },
    { day: 'Saturday', enabled: false, startTimeMins: 480, endTimeMins: 1260 },
  ],
};

export interface PartnerWorkflowConfig {
  id: string;
  enabled: boolean;
  initiationEnabled?: boolean;
  displayName?: string;
  name?: string;
  hasInitiation?: boolean;
  // Live partner-config version for this workflow. Compared against the saved
  // ruleset's partnerWorkflowVersion to detect drift (HMB-17838). Absent => 1.
  version?: number;
}

export interface IntegrationConfigEntry {
  id?: string;
  enabled: boolean;
  claimRegex?: string;
  enablementStatus?: string;
  partnerWorkflows?: PartnerWorkflowConfig[];
}

export interface Organization {
  _id: string;
  name: string;
  identityProvider: string;
  categories: {
    id: string;
    name: string;
  }[];
  branding: string[];
  oktaOrgId: string;
  domainName: string[];
  jit: boolean;
  jitAutoUpdateUserRoles: boolean;
  claimNumberInPrefixEnabled: boolean;
  phoneNumberInPrefixEnabled: boolean;
  status: string;
  linesOfBusiness: string[];
  createdAt: string;
  updatedAt: string;
  phoneConfigId: string;
  integrationConfig: {
    [key: string]: IntegrationConfigEntry;
  };
  displayName: string;
  quietHours?: QuietHoursConfig;
  activeHours?: ActiveHoursConfig;
  organizationSettings?: OrganizationSettings;
}

// Partial shape of organization.organizationSettings. Only the fields the
// UI reads are typed.
export interface OrganizationSettings {
  quietHours?: QuietHoursSettings;
  [key: string]: unknown;
}

export type CarrierConfigOptions =
  | 'sellercodes'
  | 'claimRegex'
  | 'mediaSync'
  | 'datasets'
  | 'mapping';

export interface CarrierEnablementStep {
  step:
    | 'credentials'
    | 'apiKey'
    | 'webhookSubscription'
    | 'webhookVerification'
    | 'mediaSync'
    | 'mapping'
    | 'claimRegex'
    | 'clientCode'
    | 'enable';
  stepType?: 'create' | 'update';
  config?: {
    enablementStatus?: string;
    authType?: string;
    required?: boolean;
    [key: string]: unknown; // allow for additional properties
  };
}

export interface CarrierConfigurationStep {
  step: string;
  stepType?: string;
  config?: {
    [key: string]: unknown;
  };
}

// carrierEnablement and carrierConfiguration are optional because we redact them for non-alias users
export interface IntegrationPartner {
  partnerName: string;
  enabled: boolean;
  carrierEnablement?: CarrierEnablementStep[];
  carrierConfiguration?: CarrierConfigurationStep[];
}
